'use client';
import LeftArrow from '@repo/ui/src/components/Icons/LeftArrow';
import RightArrow from '@repo/ui/src/components/Icons/RightArrow';
import { MediaType } from '@repo/ui/src/types/image';
import React from 'react';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import useEmblaCarousel from 'embla-carousel-react';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import ZoomIcon from '@repo/ui/src/components/Icons/ZoomIcon';
import Image from '@repo/ui/src/components/Image/New';
import { classNames } from '@repo/ui/src/utils/classNames';

interface PDPHeroCarouselProps {
  dark?: boolean;
  images: Array<MediaType | string>;
  openGalleryDialog: (images: Array<MediaType | string>, open: boolean) => void;
}

const PDPHeroCarousel = ({ dark, images = [], openGalleryDialog }: PDPHeroCarouselProps) => {
  const { t } = useTranslation();
  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

  React.useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentImageIndex(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  return (
    <div className="w-full h-full px-9 grid md:justify-stretch md:content-center">
      <h2 className="sr-only">
        {t('productDetailPageContent.imageGalleryText', undefined, { default: 'Image Gallery' })}:{' '}
        {images.length} {t('genericContent.imagesText', undefined, { default: 'Images' })}
      </h2>
      <div className="w-full h-full grid gap-6 grid-cols-1 max-h-[70vh]">
        <div ref={emblaRef} className="overflow-hidden">
          <div className="flex">
            {images.map((image, i) => (
              <div key={i} className="flex-[0_0_100%]">
                <div className="grid place-content-center max-h-[calc(70vh_-_108px)]">
                  <Image
                    src={typeof image === 'string' ? image : buildImageSrc(image, 800)}
                    alt={`Slide ${i + 1} of ${images.length}`}
                    className="w-full object-contain grill-image max-h-[calc(70vh_-_108px)] aspect-square"
                    height={600}
                    width={600}
                    priority
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid gap-6 grid-cols-1">
          <div className="grid grid-flow-col justify-center gap-4 items-center">
            <button
              className="border-0 bg-transparent"
              aria-label={t('genericContent.previousButtonLabel', undefined, {
                default: 'Previous',
              })}
              onClick={() => emblaApi?.scrollPrev()}
            >
              <LeftArrow
                className={classNames(
                  'block focus-visible:outline-2 focus-visible:outline-brand-300 hover:text-brand-100',
                  dark
                    ? 'text-warm-400 focus:text-white hover:text-white'
                    : 'text-warm-500 focus:text-warm-500 hover:text-warm-500',
                )}
              />
            </button>
            <button
              className="border-0 bg-transparent"
              aria-label={t('genericContent.nextButtonLabel', undefined, { default: 'Next' })}
              onClick={() => emblaApi?.scrollNext()}
            >
              <RightArrow
                className={classNames(
                  'block focus-visible:outline-2 focus-visible:outline-brand-300 hover:text-brand-100',
                  dark
                    ? 'text-warm-400 focus:text-white hover:text-white'
                    : 'text-warm-500 focus:text-warm-500 hover:text-warm-500',
                )}
              />
            </button>
            <button
              className={classNames(
                'hover:text-brand-100',
                dark
                  ? 'text-warm-400 focus:text-white hover:text-white'
                  : 'text-warm-500 focus:text-warm-500 hover:text-warm-500',
              )}
              aria-label={t('productDetailPageContent.zoomButtonLabel', undefined, {
                default: 'Open Image Gallery Dialog',
              })}
              onClick={() =>
                openGalleryDialog(
                  [...images.slice(currentImageIndex), ...images.slice(0, currentImageIndex)],
                  true,
                )
              }
            >
              <ZoomIcon className="block" decorative />
            </button>
          </div>
          <div className="flex z-index-30 list-none justify-center text-[0] py-2 gap-2">
            {images.map((_, i) => (
              <button
                key={i}
                aria-label={`Go to Slide ${i + 1}`}
                aria-current={currentImageIndex === i}
                className={classNames(
                  'w-12 flex-shrink relative py-3 after:absolute after:block after:inset-x-0 after:top-2.5 after:h-1 after:transition-colors after:duration-300 after:transition-ease-out focus-visible:outline-1 focus-visible:outline-neutral-100 border-0 bg-transparent',
                  dark ? 'hover:after:bg-white' : 'hover:after:bg-warm-500',
                  currentImageIndex === i ? 'after:bg-brand-300' : 'after:bg-warm-400',
                )}
                onClick={() => emblaApi?.scrollTo(i)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
export default PDPHeroCarousel;
